import { useState, useEffect } from "react";
import { QuestionResponse } from "@/pages/deals/types";
import { ReservedUserIds } from "@/entities/users/types";
import { useUsers } from "@/entities/users/hooks";
import { useAPIMutation } from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import { IndicationData, resolveAction } from "./types";

export const useIndicationData = (
  questionResponse: QuestionResponse | null
): IndicationData => {
  const userIds = questionResponse?.lastEditedBy
    ? [questionResponse.lastEditedBy]
    : [];
  const { users } = useUsers(userIds) || { users: [] };
  const [lastEditedByData] = users;

  const isLastEditedByDreamer =
    questionResponse?.lastEditedBy ===
    ReservedUserIds.DREAMER_CONVERSATIONAL_ID;

  const hasRevisions = Boolean(
    questionResponse?.revisions && questionResponse?.revisions?.length > 0
  );

  const revisionMadeByDreamer = questionResponse?.revisions?.find(
    (revision) =>
      revision?.lastEditedBy === ReservedUserIds.DREAMER_CONVERSATIONAL_ID
  );

  const candidateId = questionResponse?.response?.candidateId;
  const responseValue = questionResponse?.response?.value;
  // candidateName is not gated on: the backend populates candidate fields off
  // person_id alone, so a real mismatch can arrive without a resolved name.
  const hasMismatch = Boolean(
    candidateId &&
      responseValue !== null &&
      responseValue !== undefined &&
      candidateId !== responseValue
  );

  // Timestamp the active indication variant displays. Its meaning follows the branch:
  // when Dreamer authored the value it is the fill time; on a mismatch it is the
  // detection time (the suggestion arrives as a new version that keeps the human
  // author, so there is no Dreamer-authored row to read); otherwise it is when
  // Dreamer last filled the field before a human overwrote it.
  const indicationDate =
    isLastEditedByDreamer || hasMismatch
      ? questionResponse?.createdAt
      : revisionMadeByDreamer?.createdAt;

  return {
    lastEditedByData,
    isLastEditedByDreamer,
    hasRevisions,
    hasMismatch,
    revisionMadeByDreamer,
    indicationDate
  };
};

export const usePopoverState = () => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showPreviousVersion, setShowPreviousVersion] = useState(false);

  useEffect(() => {
    if (!isPopoverOpen) {
      setShowPreviousVersion(false);
    }
  }, [isPopoverOpen]);

  return {
    isPopoverOpen,
    setIsPopoverOpen,
    showPreviousVersion,
    setShowPreviousVersion
  };
};

export const useResolveMismatchMutation = () => {
  const queryClient = useQueryClient();

  return useAPIMutation<
    {
      pathParams: {
        dealId: string;
        questionId: string;
      };
      data: {
        action: resolveAction;
      };
    },
    QuestionResponse
  >(
    "deals/resolveQuestion",
    {
      method: "POST"
    },
    (_response, variables) => {
      // Invalidate questionnaire responses to trigger refetch
      queryClient.invalidateQueries({
        queryKey: ["deals/questionnaireResponses"],
        predicate: (query) => {
          const queryKey = query.queryKey as [
            string,
            { pathParams?: { dealId?: string } }?
          ];
          return (
            queryKey[0] === "deals/questionnaireResponses" &&
            queryKey[1]?.pathParams?.dealId === variables.pathParams.dealId
          );
        }
      });
    }
  );
};
